import { isoZ, z } from 'my-zod'
import { customDateErrorMap, enumOptionsToObject, activeProductLineOptions } from '../../internal'
import { programAgreementAdditionalSigner } from './program-agreement-additional-signers'

// Stages
export const programAgreementStageOptions = ['Draft', 'OutForSignature', 'Signed', 'Active', 'Archived', 'Void'] as const
export const programAgreementStages = enumOptionsToObject(programAgreementStageOptions)
export type ProgramAgreementStage = (typeof programAgreementStageOptions)[number]
export const programAgreementStageLabels: Record<ProgramAgreementStage, string> = {
  Draft: 'Draft',
  OutForSignature: 'Out for Signature',
  Signed: 'Signed',
  Active: 'Active',
  Archived: 'Archived',
  Void: 'Void'
}
export const ProgramAgreementStageEnum = z.enum(programAgreementStageOptions).setEnumLabels(programAgreementStageLabels)

// Outfitter Legal Entity Types
export const outfitterLegalEntityTypeOptions = ['LLC', 'SoleProprietorship', 'Corporation', 'LP', 'LLP'] as const
export const outfitterLegalEntityTypes = enumOptionsToObject(outfitterLegalEntityTypeOptions)
export type OutfitterLegalEntityType = (typeof outfitterLegalEntityTypeOptions)[number]
export const outfitterLegalEntityTypeLabels: Record<OutfitterLegalEntityType, string> = {
  LLC: 'LLC',
  SoleProprietorship: 'Sole Proprietorship',
  Corporation: 'Corporation',
  LP: 'LP',
  LLP: 'LLP'
}
export const outfitterLegalEntityTypesEnum = z
  .enum(outfitterLegalEntityTypeOptions)
  .setEnumLabels(outfitterLegalEntityTypeLabels)

// Program Options
export const programOptionOptions = [
  'Polaris Adventures Rentals',
  'Polaris Adventures Rentals & Polaris Adventures Select'
] as const
export const programOptions = enumOptionsToObject(programOptionOptions)
export type ProgramOption = (typeof programOptionOptions)[number]
export const programOptionsEnum = z.enum(programOptionOptions)

// Void Reasons
export const programAgreementVoidReasonOptions = [
  'externalRenegotiating',
  'externalDealLost',
  'internalNotApproved',
  'internalAdjustment',
  'internalNotAFit'
] as const
export const programAgreementVoidReasons = enumOptionsToObject(programAgreementVoidReasonOptions)
export type ProgramAgreementVoidReason = (typeof programAgreementVoidReasonOptions)[number]
export const programAgreementVoidReasonLabels: Record<ProgramAgreementVoidReason, string> = {
  externalRenegotiating: 'External - Renegotiating',
  externalDealLost: 'External - Deal Lost',
  internalNotApproved: 'Internal - Not Approved',
  internalAdjustment: 'Internal - Adjustment',
  internalNotAFit: 'Internal - Not a fit'
}
export const programAgreementVoidReasonEnum = z
  .enum(programAgreementVoidReasonOptions)
  .setEnumLabels(programAgreementVoidReasonLabels)

export const programAgreementModel = z.object({
  id: z.string().uuid(),
  shortId: z.string(),
  stage: ProgramAgreementStageEnum,
  agreementDate: isoZ.date({ errorMap: customDateErrorMap }),
  outfitterId: z
    .number({
      invalid_type_error: 'Required',
      required_error: 'Required'
    })
    .int()
    .positive('Required'),
  outfitterState: z
    .string({
      invalid_type_error: 'Required',
      required_error: 'Required'
    })
    .min(1, 'Required')
    .max(45, 'Must contain no more than 45 characters'),
  outfitterLegalEntityType: outfitterLegalEntityTypesEnum,
  outfitterFullBusinessAddress: z
    .string({
      invalid_type_error: 'Required',
      required_error: 'Required'
    })
    .min(1, 'Required')
    .max(255, 'Must contain no more than 255 characters'),
  outfitterFullOperationsAddress: z
    .string({
      invalid_type_error: 'Required',
      required_error: 'Required'
    })
    .min(1, 'Required')
    .max(255, 'Must contain no more than 255 characters'),
  productLines: z.enum(activeProductLineOptions).array().min(1, 'Required'),
  programOptions: programOptionsEnum,
  ownerId: z.string().uuid().nullable(),
  signers: programAgreementAdditionalSigner.omit({ programAgreementId: true }).array(),
  counterSignerEmail: z
    .string({
      invalid_type_error: 'Required',
      required_error: 'Required'
    })
    .min(1, 'Required'),
  signedPdf: z.string().nullable(),
  signedDate: isoZ.instant().nullable(),
  voidReason: programAgreementVoidReasonEnum.nullable(),
  createdDate: isoZ.instant(),
  lastModifiedDate: isoZ.instant(),
  deletedDate: isoZ.instant().nullable()
})

export type programAgreementModel = z.infer<typeof programAgreementModel>
